import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { GlobalService } from 'src/app/api/global.service';
import { ProjectsService, RepeatRules } from 'src/app/api/projects.service';
import * as dayjs from 'dayjs';

@Component({
  selector: 'app-repeat-rules',
  templateUrl: './repeat-rules.component.html',
  styleUrls: ['./repeat-rules.component.scss'],
})
export class RepeatRulesComponent implements OnInit {

@Input() hide_repeat_start_date: boolean = false;
@Input() fix_on_load = true;
  @Input() repeat_rules: RepeatRules = {
    version: "1",
    // Basic settings
    frequency: 'monthly',
    interval: 1,
    pattern: 'monthly',

    // Weekly settings
    weekdays: [false, false, false, false, false, false, false],

    // Monthly settings
    monthly_type: 'day',
    months: [true, true, true, true, true, true, true, true, true, true, true, true],
    monthly_any: true,
    monthly_day: 1,
    monthly_ordinal: '1',
    monthly_weekday: '1',
    monthly_special: 'start',

    // Yearly settings
    yearly_month: 1,
    yearly_day: 1,
    yearly_use_ordinal: false,
    yearly_ordinal: '1',
    yearly_weekday: '1',
    yearly_ordinal_month: 1,

    // Custom/Advanced settings
    custom: '',

    // End repeat settings
    end_type: 'never',
    end_date: null,
    end_after: 1,

    // Semi-monthly settings
    semi_monthly_first_day: 1,
    semi_monthly_second_day: 15,
    repeat_start_date: '2026-01-01',
    // Default: every date falls in the repeat date's own month - start dates on the start of the month,
    // end dates on the end of it ("Start/End of Month" needs the offset unit to be Month(s)).
    internal_end_offset_unit: 'month',
    internal_start_offset_unit: 'month',
    external_start_offset_unit: 'month',
    external_end_offset_unit: 'month',
    period_start_offset_unit: 'month',
    period_end_offset_unit: 'month',
    period_start_offset: 0,
    period_end_offset: 0,
    relative_date_type: "project_repeat_date",
    internal_start_offset: 0,
    internal_end_offset: 0,
    external_start_offset: 0,
    external_end_offset: 0,
    internal_start_offset_type: 'start_of_month',
    internal_end_offset_type: 'end_of_month',
    external_start_offset_type: 'start_of_month',
    external_end_offset_type: 'end_of_month',
    period_start_offset_type: 'start_of_month',
    period_end_offset_type: 'end_of_month',
    internal_start_date_before: false,
    internal_end_date_before: false,
    external_start_date_before: false,
    external_end_date_before: false,
    period_start_date_before: false,
    period_end_date_before: false,
  };

  @Input() repeat: boolean = true;
  @Input() disabled: boolean = false;

  @Output() repeatRulesChange = new EventEmitter<RepeatRules>();
  @Output() fixStartDateEvent = new EventEmitter<void>();
  @Output() updateDatesEvent = new EventEmitter<void>();
  @Output() repeatDateChanged = new EventEmitter<void>();

  constructor(
    private modalController: ModalController,
    public globalService: GlobalService,
    public projectsService: ProjectsService
  ) { }

  ngOnInit() {
    if (!this.repeat_rules.repeat_start_date) {
      this.repeat_rules.repeat_start_date = dayjs().format('YYYY-MM-DD');
    }
    if (!this.repeat_rules.custom_start_date && this.repeat_rules.relative_date_type === 'project_repeat_date') {
      this.repeat_rules.repeat_start_date = this.projectsService.GetNextValidDateFromToday(this.repeat_rules);
    } else if (this.fix_on_load) {
      this.FixStartDate(true);
    }
  }

  // Event handlers that emit to parent
  onRepeatRulesChange() {
    this.syncAutoStartDate();
    this.repeatRulesChange.emit(this.repeat_rules);
  }

  onFixStartDate() {
    this.fixStartDateEvent.emit();
  }

  onRepeatDateChanged() {
    this.repeatDateChanged.emit();
  }

  onUpdateDates() {
    this.syncAutoStartDate();
    this.updateDatesEvent.emit();
  }

  private syncAutoStartDate() {
    if (!this.repeat_rules.custom_start_date && this.repeat_rules.relative_date_type === 'project_repeat_date') {
      this.repeat_rules.repeat_start_date = this.projectsService.GetNextValidDateFromToday(this.repeat_rules);
    }
  }

  // Validation functions
  HasSelectedMonths(): boolean {
    if (this.repeat_rules.frequency !== 'monthly') {
      return true;
    }
    if (this.repeat_rules.monthly_any) return true;
    // Check if any month is selected in the months array
    if (Array.isArray(this.repeat_rules.months) && this.repeat_rules.months.some(m => m)) {
      return true;
    }
    return false;
  }

  HasSelectedWeekdays(): boolean {
    if (this.repeat_rules.frequency !== 'weekly') {
      return true;
    }
    // Check if any weekday is selected in the weekdays array
    if (Array.isArray(this.repeat_rules.weekdays) && this.repeat_rules.weekdays.some(w => w)) {
      return true;
    }
    return false;
  }

  // Frequency change handler
  FrequencyChange(): void {
    if (this.repeat_rules.frequency == 'daily') {
      this.repeat_rules.interval = 1;
    }
    else if (this.repeat_rules.frequency == 'weekly') {
      this.repeat_rules.interval = 1;
    }
    else if (this.repeat_rules.frequency == 'monthly') {
      this.repeat_rules.interval = 1;
    }
    else if (this.repeat_rules.frequency == 'quarterly') {
      this.repeat_rules.interval = 3;
    }
    else if (this.repeat_rules.frequency == 'semi-monthly') {
      this.repeat_rules.interval = 1;
      this.repeat_rules.semi_monthly_first_day = 1;
      this.repeat_rules.semi_monthly_second_day = 15;
    }
    else if (this.repeat_rules.frequency == 'semi-annually') {
      this.repeat_rules.interval = 6;
    }
    else if (this.repeat_rules.frequency == 'yearly') {
      this.repeat_rules.interval = 1;
    }
    this.onRepeatRulesChange();
  }

  // Date change handlers
  RelativeDateChange(): void {
    if (this.repeat_rules.relative_date_type === 'year_end_date') {
      this.repeat_rules.repeat_start_date = dayjs().startOf('month').format('YYYY-MM-DD');
      const disabledForYearEnd = ['daily', 'weekly', 'semi-monthly'];
      if (disabledForYearEnd.includes(this.repeat_rules.frequency)) {
        this.repeat_rules.frequency = 'monthly';
        this.FrequencyChange();
      }
    } else {
      this.repeat_rules.repeat_start_date = dayjs().format('YYYY-MM-DD');
    }
    this.onUpdateDates();
  }

  UpdateDates(): void {
    console.log("UpdateDates called");
    if (!this.repeat || this.disabled) return;

    const selectedYear = dayjs(this.repeat_rules.repeat_start_date).year();
    const selectedDay = dayjs(this.repeat_rules.repeat_start_date).date();
    const selectedMonth = dayjs(this.repeat_rules.repeat_start_date).month() + 1; // month is 0-indexed in dayjs

    this.repeat_rules.monthly_day = selectedDay;
    this.repeat_rules.yearly_day = selectedDay;
    this.repeat_rules.yearly_month = selectedMonth;
    
    //Set the day of the week
    for (let i = 0; i < 7; i++) {
      if (this.repeat_rules.weekdays[i]) {
        this.repeat_rules.monthly_weekday = i.toString();
        this.repeat_rules.yearly_weekday = i.toString();
      }
    }

    // Determine the ordinal (1st, 2nd, 3rd, 4th, or last) and weekday for the selected day in the month
    const selectedDate = dayjs(this.repeat_rules.repeat_start_date);
    const weekday = selectedDate.day(); // 0 (Sunday) - 6 (Saturday)
    const month = selectedDate.month();
    const year = selectedDate.year();

    // Find all dates in the month that are the same weekday
    const dates: number[] = [];
    let d = dayjs(new Date(year, month, 1));
    while (d.month() === month) {
      if (d.day() === weekday) {
        dates.push(d.date());
      }
      d = d.add(1, 'day');
    }

    // Find the ordinal (1-based index) of the selected day among those weekdays
    let ordinal = dates.findIndex(date => date === selectedDate.date()) + 1;
    // If it's the last occurrence of that weekday in the month, set ordinal to '5'
    if (dates[dates.length - 1] === selectedDate.date()) {
      ordinal = 5; // '5' means 'last'
    }

    this.repeat_rules.yearly_ordinal = ordinal.toString();
    this.repeat_rules.yearly_weekday = weekday.toString();
    this.repeat_rules.monthly_ordinal = ordinal.toString();
    this.repeat_rules.monthly_weekday = weekday.toString();
    this.repeat_rules.yearly_ordinal_month = selectedMonth;

    this.onUpdateDates();
    this.onRepeatDateChanged();
  }

  // Fix start date logic
  FixStartDate(update_date: boolean): string | null {
    let val = this.projectsService.FixStartDate(update_date, this.repeat_rules);
    console.log("FixStartDate called, returned value: ", val);
    this.onRepeatRulesChange();
    return val;

  }



  GetFixedStartDate() {
    if (!this.repeat_rules.custom_start_date && this.repeat_rules.relative_date_type === 'project_repeat_date') {
      return this.projectsService.GetNextValidDateFromToday(this.repeat_rules);
    }
    const val = this.projectsService.FixStartDate(false, this.repeat_rules);
    return val ?? this.repeat_rules.repeat_start_date;
  }

  OnCustomStartDateChange() {
    if (!this.repeat_rules.custom_start_date) {
      this.FixStartDate(true);
      this.onUpdateDates();
    }
    this.onRepeatRulesChange();
  }

  // Helper method to get repeat rules description for display
  getRepeatRulesDescription(): string {
    if (!this.repeat_rules) return 'No repeat';

    let description = '';

    switch (this.repeat_rules.frequency) {
      case 'daily':
        description = `Every ${this.repeat_rules.interval} day${this.repeat_rules.interval > 1 ? 's' : ''}`;
        break;
      case 'weekly':
        const selectedDays = this.repeat_rules.weekdays
          .map((selected, index) => selected ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index] : null)
          .filter(day => day !== null);
        description = `Every ${this.repeat_rules.interval} week${this.repeat_rules.interval > 1 ? 's' : ''} on ${selectedDays.join(', ')}`;
        break;
      case 'monthly':
        if (this.repeat_rules.monthly_type === 'day') {
          description = `Every ${this.repeat_rules.interval} month${this.repeat_rules.interval > 1 ? 's' : ''} on day ${this.repeat_rules.monthly_day}`;
        } else if (this.repeat_rules.monthly_type === 'weekday') {
          const ordinal = this.repeat_rules.monthly_ordinal === '5' ? 'Last' : this.getOrdinalSuffix(Number(this.repeat_rules.monthly_ordinal));
          const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][Number(this.repeat_rules.monthly_weekday)];
          description = `Every ${this.repeat_rules.interval} month${this.repeat_rules.interval > 1 ? 's' : ''} on the ${ordinal} ${weekday}`;
        } else if (this.repeat_rules.monthly_type === 'special') {
          const specialDesc = this.repeat_rules.monthly_special === 'start' ? 'start of month' : 'end of month';
          description = `Every ${this.repeat_rules.interval} month${this.repeat_rules.interval > 1 ? 's' : ''} on the ${specialDesc}`;
        }
        break;
      case 'yearly':
        if (this.repeat_rules.yearly_use_ordinal) {
          const ordinal = this.repeat_rules.yearly_ordinal === '5' ? 'Last' : this.getOrdinalSuffix(Number(this.repeat_rules.yearly_ordinal));
          const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][Number(this.repeat_rules.yearly_weekday)];
          const month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][this.repeat_rules.yearly_ordinal_month - 1];
          description = `Every ${this.repeat_rules.interval} year${this.repeat_rules.interval > 1 ? 's' : ''} on the ${ordinal} ${weekday} of ${month}`;
        } else {
          const month = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][this.repeat_rules.yearly_month - 1];
          description = `Every ${this.repeat_rules.interval} year${this.repeat_rules.interval > 1 ? 's' : ''} on ${month} ${this.repeat_rules.yearly_day}`;
        }
        break;
      case 'semi-monthly':
        description = `Twice per month on days ${this.repeat_rules.semi_monthly_first_day} and ${this.repeat_rules.semi_monthly_second_day}`;
        break;
      case 'quarterly':
        description = `Every 3 months`;
        break;
      case 'semi-annually':
        description = `Every 6 months`;
        break;
    }

    // Add end condition
    if (this.repeat_rules.end_type === 'on' && this.repeat_rules.end_date) {
      description += ` until ${dayjs(this.repeat_rules.end_date).format('MMM D, YYYY')}`;
    } else if (this.repeat_rules.end_type === 'after') {
      description += ` for ${this.repeat_rules.end_after} occurrence${this.repeat_rules.end_after > 1 ? 's' : ''}`;
    }

    return description;
  }

  private getOrdinalSuffix(num: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const mod100 = num % 100;
    return num + (suffixes[(mod100 - 20) % 10] || suffixes[mod100] || suffixes[0]);
  }

  async Close() {
    this.modalController.dismiss(null);
  }
}